import React, { useEffect, useRef } from 'react';
import { Box, Stack } from '@mui/material';
import { MessageBubble } from './MessageBubble';
import { EmptyState } from './EmptyState';
import type { ChatMessage } from './copilotTypes';

function findScrollHost(end: HTMLElement | null): HTMLElement | null {
  if (!end || typeof getComputedStyle !== 'function') return null;
  let host = end.parentElement;
  while (host) {
    const oy = getComputedStyle(host).overflowY;
    if (oy === 'auto' || oy === 'scroll') return host;
    host = host.parentElement;
  }
  return null;
}

function scrollToBottom(host: HTMLElement, behavior: ScrollBehavior = 'smooth') {
  const top = host.scrollHeight;
  if (typeof host.scrollTo === 'function') {
    host.scrollTo({ top, behavior });
  } else {
    // scrollTo is missing in jsdom and some older embedded browsers.
    host.scrollTop = top;
  }
}

/**
 * The scrolling transcript. Renders the example questions as the empty state, the turns otherwise,
 * and keeps the latest turn anchored at the bottom as it grows/streams.
 */
export function MessageList({
  messages,
  onExample,
  onStop,
  onRetry,
  onFollowup,
  onEdit,
}: {
  messages: ChatMessage[];
  onExample: (q: string) => void;
  /** Abort the in-flight request (Stop on a streaming turn). */
  onStop?: () => void;
  /** Re-run the question for an errored/cancelled assistant turn (by its message id). */
  onRetry?: (assistantId: string) => void;
  /** Re-ask a follow-up chip. */
  onFollowup?: (q: string) => void;
  /** Load a user prompt into the composer for editing. */
  onEdit?: (text: string) => void;
}) {
  const endRef = useRef<HTMLDivElement | null>(null);

  // Keep the newest turn in view whenever the rendered response changes. This includes the user's new
  // turn, streamed tokens, record-count/query updates, and the final answer mounting.
  const last = messages[messages.length - 1];
  const lastStatus = last?.status;
  const lastHasAnswer = !!last?.answer;
  const lastText = last?.text;
  const lastCount = last?.count;
  const lastSuggestions = last?.suggestions;
  useEffect(() => {
    // Scroll the TRANSCRIPT CONTAINER only — never the window. scrollIntoView() scrolls EVERY scrollable
    // ancestor, so it scrolled the whole dashboard up/around whenever the Copilot panel sat below the fold.
    // Walk up to the bounded overflow:auto host and set its scrollTop, so the page itself never moves.
    const host = findScrollHost(endRef.current);
    if (!host) return;
    // Always follow the active response, even if the user had scrolled up before asking. This keeps a
    // new query and its response visible, matching the expected behavior of a chatbot transcript.
    scrollToBottom(host);
  }, [messages.length, lastStatus, lastHasAnswer, lastText, lastCount, lastSuggestions]);

  // The answer's chart slot can grow after the result arrives while the handoff and chart build settle.
  // Observe the transcript content so each real height change gets a final bottom alignment instead of
  // leaving the viewport above the newly expanded chart.
  useEffect(() => {
    const end = endRef.current;
    const host = findScrollHost(end);
    const content = end?.parentElement;
    if (!host || !content || typeof ResizeObserver === 'undefined') return;

    let frame = 0;
    const observer = new ResizeObserver(() => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = 0;
        scrollToBottom(host, 'auto');
      });
    });
    observer.observe(content);

    return () => {
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [messages.length, lastHasAnswer]);

  if (messages.length === 0) {
    // Centered hero (orb + greeting + chips) that fills the transcript height, so the chips sit near
    // the composer rather than pinned to the top. width:100% so EmptyState centers horizontally.
    return (
      <Box sx={{ minHeight: '100%', width: '100%', display: 'flex' }}>
        <EmptyState onExample={onExample} />
      </Box>
    );
  }

  return (
    <Stack spacing={2} sx={{ px: 1, pb: 2 }}>
      {messages.map((m) => (
        <MessageBubble
          key={m.id}
          message={m}
          onStop={onStop}
          onRetry={onRetry ? () => onRetry(m.id) : undefined}
          onFollowup={onFollowup}
          onEdit={onEdit}
        />
      ))}
      <Box ref={endRef} />
    </Stack>
  );
}
